import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AppLayout from '../components/AppLayout';
import AdminPinGate from '../components/AdminPinGate';
import { useTheme } from '../contexts/ThemeContext';
import { 
  CreditCard, CheckCircle2, AlertCircle, Clock, RefreshCw,
  FileText, DollarSign, Loader2, ExternalLink, Zap, Building2,
  Search, Eye, EyeOff, Key, Shield,
  TestTube, Rocket, Copy, Check
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const StripeAdmin = () => {
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const [pinVerified, setPinVerified] = useState(false);
  const [overview, setOverview] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('api-keys');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Stripe Settings State
  const [stripeSettings, setStripeSettings] = useState(null);
  const [showStripeTestSecret, setShowStripeTestSecret] = useState(false);
  const [showStripeLiveSecret, setShowStripeLiveSecret] = useState(false);
  const [editingStripeKeys, setEditingStripeKeys] = useState(false);
  const [newStripeKeys, setNewStripeKeys] = useState({
    test_secret_key: '',
    test_publishable_key: '',
    live_secret_key: '',
    live_publishable_key: ''
  });
  const [savingKeys, setSavingKeys] = useState(false);
  const [testingConnection, setTestingConnection] = useState(false);
  const [connectionTestResult, setConnectionTestResult] = useState(null);
  const [copiedField, setCopiedField] = useState(null);
  
  // Provider Settings State
  const [providerSettings, setProviderSettings] = useState(null);
  
  // Revolut Settings State
  const [revolutSettings, setRevolutSettings] = useState(null);
  const [showRevolutSandboxSecret, setShowRevolutSandboxSecret] = useState(false);
  const [showRevolutSandboxPublic, setShowRevolutSandboxPublic] = useState(false);
  const [showRevolutProductionSecret, setShowRevolutProductionSecret] = useState(false);
  const [showRevolutProductionPublic, setShowRevolutProductionPublic] = useState(false);
  const [editingRevolutKeys, setEditingRevolutKeys] = useState(false);
  const [newRevolutKeys, setNewRevolutKeys] = useState({
    sandbox_secret_key: '',
    sandbox_public_key: '',
    production_secret_key: '',
    production_public_key: ''
  });
  const [savingRevolutKeys, setSavingRevolutKeys] = useState(false);
  const [testingRevolutConnection, setTestingRevolutConnection] = useState(false);
  const [revolutTestResult, setRevolutTestResult] = useState(null);

  useEffect(() => {
    fetchOverview();
    fetchTransactions();
    fetchStripeSettings();
    fetchProviderSettings();
    fetchRevolutSettings();
  }, []);

  const fetchOverview = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/payments/admin/overview`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setOverview(data);
      }
    } catch (err) {
      console.error('Failed to fetch overview:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchTransactions = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/payments/admin/transactions`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setTransactions(data.transactions || []);
      }
    } catch (err) {
      console.error('Failed to fetch transactions:', err);
    }
  };

  const fetchStripeSettings = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/payments/admin/stripe-settings`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setStripeSettings(data);
        setNewStripeKeys({
          test_secret_key: data.test_secret_key || '',
          test_publishable_key: data.test_publishable_key || '',
          live_secret_key: data.live_secret_key || '',
          live_publishable_key: data.live_publishable_key || ''
        });
      }
    } catch (err) {
      console.error('Failed to fetch stripe settings:', err);
    }
  };

  const fetchProviderSettings = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/payments/admin/provider-settings`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setProviderSettings(data);
      }
    } catch (err) {
      console.error('Failed to fetch provider settings:', err);
    }
  };

  const fetchRevolutSettings = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/payments/admin/revolut-settings`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setRevolutSettings(data);
        setNewRevolutKeys({
          sandbox_secret_key: data.sandbox_secret_key || '',
          sandbox_public_key: data.sandbox_public_key || '',
          production_secret_key: data.production_secret_key || '',
          production_public_key: data.production_public_key || ''
        });
      }
    } catch (err) {
      console.error('Failed to fetch revolut settings:', err);
    }
  };

  const switchActiveProvider = async (provider) => {
    setSavingKeys(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/payments/admin/active-provider`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ active_provider: provider })
      });
      if (response.ok) {
        fetchProviderSettings();
        fetchOverview();
      } else {
        const err = await response.json();
        alert(err.detail || 'Failed to switch provider');
      }
    } catch (err) {
      console.error('Failed to switch provider:', err);
    } finally {
      setSavingKeys(false);
    }
  };

  const saveStripeKeys = async () => {
    setSavingKeys(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/payments/admin/stripe-settings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(newStripeKeys)
      });
      if (response.ok) {
        const data = await response.json();
        setStripeSettings(data);
        setEditingStripeKeys(false);
        fetchOverview();
        fetchProviderSettings();
      }
    } catch (err) {
      console.error('Failed to save stripe keys:', err);
    } finally {
      setSavingKeys(false);
    }
  };

  const saveRevolutKeys = async () => {
    setSavingRevolutKeys(true);
    try {
      // Only send keys that have values (not empty strings)
      const keysToSave = {};
      if (newRevolutKeys.sandbox_secret_key?.trim()) {
        keysToSave.sandbox_secret_key = newRevolutKeys.sandbox_secret_key.trim();
      }
      if (newRevolutKeys.sandbox_public_key?.trim()) {
        keysToSave.sandbox_public_key = newRevolutKeys.sandbox_public_key.trim();
      }
      if (newRevolutKeys.production_secret_key?.trim()) {
        keysToSave.production_secret_key = newRevolutKeys.production_secret_key.trim();
      }
      if (newRevolutKeys.production_public_key?.trim()) {
        keysToSave.production_public_key = newRevolutKeys.production_public_key.trim();
      }
      
      if (Object.keys(keysToSave).length === 0) {
        setRevolutTestResult({ success: false, error: 'Please enter at least one API key' });
        setSavingRevolutKeys(false);
        return;
      }
      
      const response = await fetch(`${BACKEND_URL}/api/payments/admin/revolut-settings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(keysToSave)
      });
      if (response.ok) {
        fetchRevolutSettings();
        fetchProviderSettings();
        setEditingRevolutKeys(false);
        setRevolutTestResult({ success: true, error: null, message: 'Keys saved successfully!' });
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Failed to save revolut keys:', response.status, errorData);
        setRevolutTestResult({ 
          success: false, 
          error: errorData.detail || `Failed to save keys (${response.status})` 
        });
      }
    } catch (err) {
      console.error('Failed to save revolut keys:', err);
      setRevolutTestResult({ success: false, error: err.message || 'Network error saving keys' });
    } finally {
      setSavingRevolutKeys(false);
    }
  };

  const toggleStripeMode = async (mode) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/payments/admin/stripe-mode`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ mode })
      });
      if (response.ok) {
        fetchStripeSettings();
        fetchOverview();
        fetchProviderSettings();
      }
    } catch (err) {
      console.error('Failed to toggle stripe mode:', err);
    }
  };

  const toggleRevolutMode = async (mode) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/payments/admin/revolut-mode`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ mode })
      });
      if (response.ok) {
        fetchRevolutSettings();
        fetchProviderSettings();
      }
    } catch (err) {
      console.error('Failed to toggle revolut mode:', err);
    }
  };

  const testStripeConnection = async () => {
    setTestingConnection(true);
    setConnectionTestResult(null);
    try {
      const response = await fetch(`${BACKEND_URL}/api/payments/admin/test-connection`, {
        method: 'POST',
        credentials: 'include'
      });
      const data = await response.json();
      setConnectionTestResult(data);
    } catch (err) {
      setConnectionTestResult({ success: false, error: err.message });
    } finally {
      setTestingConnection(false);
    }
  };

  const testRevolutConnection = async () => {
    setTestingRevolutConnection(true);
    setRevolutTestResult(null);
    try {
      const response = await fetch(`${BACKEND_URL}/api/payments/admin/test-revolut-connection`, {
        method: 'POST',
        credentials: 'include'
      });
      const data = await response.json();
      setRevolutTestResult(data);
    } catch (err) {
      setRevolutTestResult({ success: false, error: err.message });
    } finally {
      setTestingRevolutConnection(false);
    }
  };

  const copyToClipboard = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const maskKey = (key) => {
    if (!key) return '';
    if (key.length < 20) return key;
    return key.substring(0, 12) + '•'.repeat(20) + key.substring(key.length - 8);
  };

  const formatCurrency = (amount) => {
    return `£${(amount || 0).toLocaleString('en-GB', { minimumFractionDigits: 2 })}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getMethodIcon = (method) => {
    switch (method) {
      case 'stripe_card':
      case 'stripe':
      case 'card':
        return <CreditCard className="w-4 h-4" />;
      case 'bank_transfer':
      case 'stripe_bank':
        return <Building2 className="w-4 h-4" />;
      default:
        return <DollarSign className="w-4 h-4" />;
    }
  };

  const filteredTransactions = transactions.filter(t =>
    t.company_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    t.transaction_id?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 text-[#A2FF00] animate-spin" />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <AdminPinGate 
        onVerified={() => setPinVerified(true)} 
        title="Payment Settings"
      >
      <div className="p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`} style={{ fontFamily: 'Unbounded, sans-serif' }}>
              Payment Settings
            </h1>
            <p className={`mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              Manage payment providers, API keys and view transactions
            </p>
          </div>
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => navigate('/contracts')}
            >
              <FileText className="w-4 h-4 mr-2" />
              View Contracts
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
          <div className={`${isDark ? 'bg-[#111] border-white/10' : 'bg-white border-gray-200'} border rounded-lg p-4`}>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-500/20 rounded-lg">
                <DollarSign className="w-5 h-5 text-green-400" />
              </div>
              <div>
                <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'} uppercase`}>Total Collected</p>
                <p className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {formatCurrency(overview?.stats?.total_collected_gbp)}
                </p>
              </div>
            </div>
          </div>

          <div className={`${isDark ? 'bg-[#111] border-white/10' : 'bg-white border-gray-200'} border rounded-lg p-4`}>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-500/20 rounded-lg">
                <Clock className="w-5 h-5 text-orange-400" />
              </div>
              <div>
                <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'} uppercase`}>Pending Setup</p>
                <p className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {overview?.stats?.pending_setup || 0}
                </p>
              </div>
            </div>
          </div>

          <div className={`${isDark ? 'bg-[#111] border-white/10' : 'bg-white border-gray-200'} border rounded-lg p-4`}>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-500/20 rounded-lg">
                <AlertCircle className="w-5 h-5 text-yellow-400" />
              </div>
              <div>
                <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'} uppercase`}>Awaiting Payment</p>
                <p className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {overview?.stats?.awaiting_payment || 0}
                </p>
              </div>
            </div>
          </div>

          <div className={`${isDark ? 'bg-[#111] border-white/10' : 'bg-white border-gray-200'} border rounded-lg p-4`}>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-500/20 rounded-lg">
                <RefreshCw className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'} uppercase`}>Active Subscriptions</p>
                <p className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {overview?.stats?.active_subscriptions || 0}
                </p>
              </div>
            </div>
          </div>

          <div className={`${isDark ? 'bg-[#111] border-white/10' : 'bg-white border-gray-200'} border rounded-lg p-4`}>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-500/20 rounded-lg">
                <AlertCircle className="w-5 h-5 text-red-400" />
              </div>
              <div>
                <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'} uppercase`}>Overdue Invoices</p>
                <p className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {overview?.stats?.overdue_invoices || 0}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className={`${isDark ? 'bg-[#111] border-white/10' : 'bg-white border-gray-200'} border rounded-lg`}>
          <div className={`flex gap-1 p-1 border-b ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
            {['api-keys', 'providers', 'transactions', 'settings'].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  activeTab === tab
                    ? 'bg-[#A2FF00] text-black'
                    : `${isDark ? 'text-gray-400 hover:text-white hover:bg-white/5' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'}`
                }`}
              >
                {tab === 'api-keys' ? 'API Keys' : tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </div>

          <div className="p-4">
            {/* API Keys Tab - Two Column Layout */}
            {activeTab === 'api-keys' && (
              <div className="grid md:grid-cols-2 gap-6">
                {/* LEFT COLUMN - STRIPE */}
                <div className="space-y-4">
                  {/* Stripe Header */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-[#635BFF] rounded-lg flex items-center justify-center">
                        <span className="text-white font-bold">S</span>
                      </div>
                      <div>
                        <h3 className={`font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Stripe</h3>
                        <div className="flex items-center gap-2">
                          {providerSettings?.stripe?.configured ? (
                            <span className="text-xs text-green-400">● Connected</span>
                          ) : (
                            <span className="text-xs text-yellow-400">● Not configured</span>
                          )}
                          <span className={`text-xs px-1.5 py-0.5 rounded ${
                            stripeSettings?.mode === 'live' 
                              ? 'bg-red-500/20 text-red-400' 
                              : 'bg-blue-500/20 text-blue-400'
                          }`}>
                            {stripeSettings?.mode === 'live' ? 'Live' : 'Test'}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={testStripeConnection}
                        disabled={testingConnection}
                      >
                        {testingConnection ? <Loader2 className="w-3 h-3 animate-spin" /> : <Zap className="w-3 h-3" />}
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => window.open('https://dashboard.stripe.com', '_blank')}
                      >
                        <ExternalLink className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>

                  {/* Stripe Test Result */}
                  {connectionTestResult && (
                    <div className={`p-2 rounded text-xs ${
                      connectionTestResult.success 
                        ? 'bg-green-500/10 text-green-400' 
                        : 'bg-red-500/10 text-red-400'
                    }`}>
                      {connectionTestResult.success 
                        ? `✓ Connected: ${connectionTestResult.account_name}`
                        : `✗ ${connectionTestResult.error}`
                      }
                    </div>
                  )}

                  {/* Stripe Mode Toggle */}
                  <div className={`p-3 rounded-lg border ${isDark ? 'border-white/10 bg-[#0A0A0A]' : 'border-gray-200 bg-gray-50'}`}>
                    <div className="flex items-center justify-between">
                      <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Mode</span>
                      <div className="flex gap-1">
                        <button
                          onClick={() => toggleStripeMode('test')}
                          className={`px-3 py-1 text-xs rounded ${
                            stripeSettings?.mode === 'test' 
                              ? 'bg-blue-500 text-white' 
                              : `${isDark ? 'bg-white/10 text-gray-400' : 'bg-gray-200 text-gray-600'}`
                          }`}
                        >
                          Test
                        </button>
                        <button
                          onClick={() => toggleStripeMode('live')}
                          className={`px-3 py-1 text-xs rounded ${
                            stripeSettings?.mode === 'live' 
                              ? 'bg-red-500 text-white' 
                              : `${isDark ? 'bg-white/10 text-gray-400' : 'bg-gray-200 text-gray-600'}`
                          }`}
                        >
                          Live
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Stripe Test API Keys */}
                  <div className={`p-4 rounded-lg border ${isDark ? 'border-blue-500/30 bg-blue-500/5' : 'border-blue-200 bg-blue-50'}`}>
                    <div className="flex items-center gap-2 mb-3">
                      <TestTube className="w-4 h-4 text-blue-400" />
                      <span className={`text-sm font-medium ${isDark ? 'text-blue-300' : 'text-blue-700'}`}>Test API Keys</span>
                    </div>
                    
                    <div className="space-y-3">
                      <div>
                        <label className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Publishable Key</label>
                        {editingStripeKeys ? (
                          <Input
                            value={newStripeKeys.test_publishable_key}
                            onChange={(e) => setNewStripeKeys({...newStripeKeys, test_publishable_key: e.target.value})}
                            placeholder="pk_test_..."
                            className={`mt-1 font-mono text-xs ${isDark ? 'bg-[#111] border-white/20' : ''}`}
                          />
                        ) : (
                          <p className={`font-mono text-xs mt-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                            {stripeSettings?.test_publishable_key ? maskKey(stripeSettings.test_publishable_key) : 'Not configured'}
                          </p>
                        )}
                      </div>
                      <div>
                        <div className="flex items-center justify-between">
                          <label className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Secret Key</label>
                          <button onClick={() => setShowStripeTestSecret(!showStripeTestSecret)} className="text-xs text-gray-400">
                            {showStripeTestSecret ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                          </button>
                        </div>
                        {editingStripeKeys ? (
                          <Input
                            value={newStripeKeys.test_secret_key}
                            onChange={(e) => setNewStripeKeys({...newStripeKeys, test_secret_key: e.target.value})}
                            placeholder="sk_test_..."
                            type={showStripeTestSecret ? 'text' : 'password'}
                            className={`mt-1 font-mono text-xs ${isDark ? 'bg-[#111] border-white/20' : ''}`}
                          />
                        ) : (
                          <p className={`font-mono text-xs mt-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                            {stripeSettings?.test_secret_key 
                              ? (showStripeTestSecret ? stripeSettings.test_secret_key : maskKey(stripeSettings.test_secret_key))
                              : 'Not configured'}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Stripe Live API Keys */}
                  <div className={`p-4 rounded-lg border ${isDark ? 'border-red-500/30 bg-red-500/5' : 'border-red-200 bg-red-50'}`}>
                    <div className="flex items-center gap-2 mb-3">
                      <Rocket className="w-4 h-4 text-red-400" />
                      <span className={`text-sm font-medium ${isDark ? 'text-red-300' : 'text-red-700'}`}>Live API Keys</span>
                    </div>
                    
                    <div className="space-y-3">
                      <div>
                        <label className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Publishable Key</label>
                        {editingStripeKeys ? (
                          <Input
                            value={newStripeKeys.live_publishable_key}
                            onChange={(e) => setNewStripeKeys({...newStripeKeys, live_publishable_key: e.target.value})}
                            placeholder="pk_live_..."
                            className={`mt-1 font-mono text-xs ${isDark ? 'bg-[#111] border-white/20' : ''}`}
                          />
                        ) : (
                          <p className={`font-mono text-xs mt-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                            {stripeSettings?.live_publishable_key ? maskKey(stripeSettings.live_publishable_key) : 'Not configured'}
                          </p>
                        )}
                      </div>
                      <div>
                        <div className="flex items-center justify-between">
                          <label className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Secret Key</label>
                          <button onClick={() => setShowStripeLiveSecret(!showStripeLiveSecret)} className="text-xs text-gray-400">
                            {showStripeLiveSecret ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                          </button>
                        </div>
                        {editingStripeKeys ? (
                          <Input
                            value={newStripeKeys.live_secret_key}
                            onChange={(e) => setNewStripeKeys({...newStripeKeys, live_secret_key: e.target.value})}
                            placeholder="sk_live_..."
                            type={showStripeLiveSecret ? 'text' : 'password'}
                            className={`mt-1 font-mono text-xs ${isDark ? 'bg-[#111] border-white/20' : ''}`}
                          />
                        ) : (
                          <p className={`font-mono text-xs mt-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                            {stripeSettings?.live_secret_key 
                              ? (showStripeLiveSecret ? stripeSettings.live_secret_key : maskKey(stripeSettings.live_secret_key))
                              : 'Not configured'}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Stripe Webhook URL */}
                  <div className={`p-3 rounded-lg border ${isDark ? 'border-white/10 bg-[#0A0A0A]' : 'border-gray-200 bg-gray-50'}`}>
                    <div className="flex items-center justify-between mb-1">
                      <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Webhook URL</span>
                      <button
                        onClick={() => copyToClipboard(`${BACKEND_URL}/api/webhook/stripe`, 'stripe_webhook')}
                        className="text-xs text-gray-400 hover:text-[#A2FF00]"
                      >
                        {copiedField === 'stripe_webhook' ? <Check className="w-3 h-3 text-green-400" /> : <Copy className="w-3 h-3" />}
                      </button>
                    </div>
                    <p className={`font-mono text-xs ${isDark ? 'text-[#A2FF00]' : 'text-green-600'} break-all`}>
                      {BACKEND_URL}/api/webhook/stripe
                    </p>
                  </div>

                  {/* Stripe Edit/Save Buttons */}
                  <div className="flex justify-end gap-2">
                    {editingStripeKeys ? (
                      <>
                        <Button variant="outline" size="sm" onClick={() => setEditingStripeKeys(false)}>Cancel</Button>
                        <Button 
                          size="sm"
                          className="bg-[#635BFF] hover:bg-[#5851DB]"
                          onClick={saveStripeKeys}
                          disabled={savingKeys}
                        >
                          {savingKeys ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Check className="w-3 h-3 mr-1" />}
                          Save
                        </Button>
                      </>
                    ) : (
                      <Button size="sm" variant="outline" onClick={() => setEditingStripeKeys(true)}>
                        <Key className="w-3 h-3 mr-1" />
                        Edit Keys
                      </Button>
                    )}
                  </div>
                </div>

                {/* RIGHT COLUMN - REVOLUT */}
                <div className="space-y-4">
                  {/* Revolut Header */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-[#0075EB] rounded-lg flex items-center justify-center">
                        <span className="text-white font-bold">R</span>
                      </div>
                      <div>
                        <h3 className={`font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Revolut</h3>
                        <div className="flex items-center gap-2">
                          {providerSettings?.revolut?.configured ? (
                            <span className="text-xs text-green-400">● Connected</span>
                          ) : (
                            <span className="text-xs text-yellow-400">● Not configured</span>
                          )}
                          <span className={`text-xs px-1.5 py-0.5 rounded ${
                            revolutSettings?.mode === 'production' 
                              ? 'bg-red-500/20 text-red-400' 
                              : 'bg-blue-500/20 text-blue-400'
                          }`}>
                            {revolutSettings?.mode === 'production' ? 'Production' : 'Sandbox'}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={testRevolutConnection}
                        disabled={testingRevolutConnection}
                      >
                        {testingRevolutConnection ? <Loader2 className="w-3 h-3 animate-spin" /> : <Zap className="w-3 h-3" />}
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => window.open('https://business.revolut.com', '_blank')}
                      >
                        <ExternalLink className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>

                  {/* Revolut Test Result */}
                  {revolutTestResult && (
                    <div className={`p-2 rounded text-xs ${
                      revolutTestResult.success 
                        ? 'bg-green-500/10 text-green-400' 
                        : 'bg-red-500/10 text-red-400'
                    }`}>
                      {revolutTestResult.success 
                        ? `✓ ${revolutTestResult.message || `Connected: ${revolutTestResult.environment || 'Success'}`}`
                        : `✗ ${revolutTestResult.error}`
                      }
                    </div>
                  )}

                  {/* Revolut Mode Toggle */}
                  <div className={`p-3 rounded-lg border ${isDark ? 'border-white/10 bg-[#0A0A0A]' : 'border-gray-200 bg-gray-50'}`}>
                    <div className="flex items-center justify-between">
                      <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Mode</span>
                      <div className="flex gap-1">
                        <button
                          onClick={() => toggleRevolutMode('test')}
                          className={`px-3 py-1 text-xs rounded ${
                            revolutSettings?.mode === 'sandbox' 
                              ? 'bg-blue-500 text-white' 
                              : `${isDark ? 'bg-white/10 text-gray-400' : 'bg-gray-200 text-gray-600'}`
                          }`}
                        >
                          Sandbox
                        </button>
                        <button
                          onClick={() => toggleRevolutMode('live')}
                          className={`px-3 py-1 text-xs rounded ${
                            revolutSettings?.mode === 'production' 
                              ? 'bg-red-500 text-white' 
                              : `${isDark ? 'bg-white/10 text-gray-400' : 'bg-gray-200 text-gray-600'}`
                          }`}
                        >
                          Production
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Revolut Sandbox API Keys */}
                  <div className={`p-4 rounded-lg border ${isDark ? 'border-blue-500/30 bg-blue-500/5' : 'border-blue-200 bg-blue-50'}`}>
                    <div className="flex items-center gap-2 mb-3">
                      <TestTube className="w-4 h-4 text-blue-400" />
                      <span className={`text-sm font-medium ${isDark ? 'text-blue-300' : 'text-blue-700'}`}>Sandbox API Keys</span>
                    </div>
                    
                    <div className="space-y-3">
                      {/* Sandbox Secret Key */}
                      <div>
                        <div className="flex items-center justify-between">
                          <label className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Secret Key</label>
                          <button onClick={() => setShowRevolutSandboxSecret(!showRevolutSandboxSecret)} className="text-xs text-gray-400">
                            {showRevolutSandboxSecret ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                          </button>
                        </div>
                        {editingRevolutKeys ? (
                          <Input
                            value={newRevolutKeys.sandbox_secret_key}
                            onChange={(e) => setNewRevolutKeys({...newRevolutKeys, sandbox_secret_key: e.target.value})}
                            placeholder="sk_sandbox_..."
                            type={showRevolutSandboxSecret ? 'text' : 'password'}
                            className={`mt-1 font-mono text-xs ${isDark ? 'bg-[#111] border-white/20' : ''}`}
                          />
                        ) : (
                          <p className={`font-mono text-xs mt-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                            {revolutSettings?.sandbox_secret_key 
                              ? (showRevolutSandboxSecret ? revolutSettings.sandbox_secret_key : maskKey(revolutSettings.sandbox_secret_key))
                              : 'Not configured'}
                          </p>
                        )}
                      </div>
                      
                      {/* Sandbox Public Key */}
                      <div>
                        <div className="flex items-center justify-between">
                          <label className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Public Key</label>
                          <button onClick={() => setShowRevolutSandboxPublic(!showRevolutSandboxPublic)} className="text-xs text-gray-400">
                            {showRevolutSandboxPublic ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                          </button>
                        </div>
                        {editingRevolutKeys ? (
                          <Input
                            value={newRevolutKeys.sandbox_public_key}
                            onChange={(e) => setNewRevolutKeys({...newRevolutKeys, sandbox_public_key: e.target.value})}
                            placeholder="pk_sandbox_..."
                            type={showRevolutSandboxPublic ? 'text' : 'password'}
                            className={`mt-1 font-mono text-xs ${isDark ? 'bg-[#111] border-white/20' : ''}`}
                          />
                        ) : (
                          <p className={`font-mono text-xs mt-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                            {revolutSettings?.sandbox_public_key 
                              ? (showRevolutSandboxPublic ? revolutSettings.sandbox_public_key : maskKey(revolutSettings.sandbox_public_key))
                              : 'Not configured'}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Revolut Production API Keys */}
                  <div className={`p-4 rounded-lg border ${isDark ? 'border-red-500/30 bg-red-500/5' : 'border-red-200 bg-red-50'}`}>
                    <div className="flex items-center gap-2 mb-3">
                      <Rocket className="w-4 h-4 text-red-400" />
                      <span className={`text-sm font-medium ${isDark ? 'text-red-300' : 'text-red-700'}`}>Production API Keys</span>
                    </div>
                    
                    <div className="space-y-3">
                      {/* Production Secret Key */}
                      <div>
                        <div className="flex items-center justify-between">
                          <label className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Secret Key</label>
                          <button onClick={() => setShowRevolutProductionSecret(!showRevolutProductionSecret)} className="text-xs text-gray-400">
                            {showRevolutProductionSecret ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                          </button>
                        </div>
                        {editingRevolutKeys ? (
                          <Input
                            value={newRevolutKeys.production_secret_key}
                            onChange={(e) => setNewRevolutKeys({...newRevolutKeys, production_secret_key: e.target.value})}
                            placeholder="sk_live_..."
                            type={showRevolutProductionSecret ? 'text' : 'password'}
                            className={`mt-1 font-mono text-xs ${isDark ? 'bg-[#111] border-white/20' : ''}`}
                          />
                        ) : (
                          <p className={`font-mono text-xs mt-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                            {revolutSettings?.production_secret_key 
                              ? (showRevolutProductionSecret ? revolutSettings.production_secret_key : maskKey(revolutSettings.production_secret_key))
                              : 'Not configured'}
                          </p>
                        )}
                      </div>
                      
                      {/* Production Public Key */}
                      <div>
                        <div className="flex items-center justify-between">
                          <label className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Public Key</label>
                          <button onClick={() => setShowRevolutProductionPublic(!showRevolutProductionPublic)} className="text-xs text-gray-400">
                            {showRevolutProductionPublic ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                          </button>
                        </div>
                        {editingRevolutKeys ? (
                          <Input
                            value={newRevolutKeys.production_public_key}
                            onChange={(e) => setNewRevolutKeys({...newRevolutKeys, production_public_key: e.target.value})}
                            placeholder="pk_live_..."
                            type={showRevolutProductionPublic ? 'text' : 'password'}
                            className={`mt-1 font-mono text-xs ${isDark ? 'bg-[#111] border-white/20' : ''}`}
                          />
                        ) : (
                          <p className={`font-mono text-xs mt-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                            {revolutSettings?.production_public_key 
                              ? (showRevolutProductionPublic ? revolutSettings.production_public_key : maskKey(revolutSettings.production_public_key))
                              : 'Not configured'}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Revolut Webhook URL */}
                  <div className={`p-3 rounded-lg border ${isDark ? 'border-white/10 bg-[#0A0A0A]' : 'border-gray-200 bg-gray-50'}`}>
                    <div className="flex items-center justify-between mb-1">
                      <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Webhook URL</span>
                      <button
                        onClick={() => copyToClipboard(`${BACKEND_URL}/api/webhook/revolut`, 'revolut_webhook')}
                        className="text-xs text-gray-400 hover:text-[#0075EB]"
                      >
                        {copiedField === 'revolut_webhook' ? <Check className="w-3 h-3 text-green-400" /> : <Copy className="w-3 h-3" />}
                      </button>
                    </div>
                    <p className={`font-mono text-xs ${isDark ? 'text-[#0075EB]' : 'text-blue-600'} break-all`}>
                      {BACKEND_URL}/api/webhook/revolut
                    </p>
                  </div>

                  {/* Revolut Edit/Save Buttons */}
                  <div className="flex justify-end gap-2">
                    {editingRevolutKeys ? (
                      <>
                        <Button variant="outline" size="sm" onClick={() => setEditingRevolutKeys(false)}>Cancel</Button>
                        <Button 
                          size="sm"
                          className="bg-[#0075EB] hover:bg-[#0066CC]"
                          onClick={saveRevolutKeys}
                          disabled={savingRevolutKeys}
                        >
                          {savingRevolutKeys ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Check className="w-3 h-3 mr-1" />}
                          Save
                        </Button>
                      </>
                    ) : (
                      <Button size="sm" variant="outline" onClick={() => setEditingRevolutKeys(true)}>
                        <Key className="w-3 h-3 mr-1" />
                        Edit Keys
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Providers Tab - Provider Selection Only */}
            {activeTab === 'providers' && (
              <div className="space-y-6">
                <div>
                  <h3 className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'} mb-2`}>Active Payment Provider</h3>
                  <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'} mb-6`}>
                    Select which payment provider to use for processing payments. Configure API keys in the API Keys tab.
                  </p>
                  
                  <div className="grid md:grid-cols-2 gap-4">
                    {/* Stripe Card */}
                    <div 
                      className={`p-6 rounded-lg border-2 cursor-pointer transition-all ${
                        providerSettings?.active_provider === 'stripe'
                          ? 'border-[#A2FF00] bg-[#A2FF00]/10'
                          : `${isDark ? 'border-white/20 hover:border-white/40' : 'border-gray-200 hover:border-gray-400'}`
                      }`}
                      onClick={() => switchActiveProvider('stripe')}
                    >
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 bg-[#635BFF] rounded-lg flex items-center justify-center">
                            <span className="text-white font-bold text-lg">S</span>
                          </div>
                          <div>
                            <h4 className={`font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Stripe</h4>
                            <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Card & Bank payments</p>
                          </div>
                        </div>
                        {providerSettings?.active_provider === 'stripe' && (
                          <span className="px-2 py-1 rounded text-xs bg-[#A2FF00] text-black font-medium">ACTIVE</span>
                        )}
                      </div>
                      <div className="space-y-2 text-sm">
                        <div className="flex items-center gap-2">
                          {providerSettings?.stripe?.configured ? (
                            <CheckCircle2 className="w-4 h-4 text-green-400" />
                          ) : (
                            <AlertCircle className="w-4 h-4 text-yellow-400" />
                          )}
                          <span className={isDark ? 'text-gray-300' : 'text-gray-600'}>
                            {providerSettings?.stripe?.configured ? 'API Keys Configured' : 'API Keys Not Configured'}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`px-2 py-0.5 rounded text-xs ${
                            providerSettings?.stripe?.mode === 'live' 
                              ? 'bg-red-500/20 text-red-400' 
                              : 'bg-blue-500/20 text-blue-400'
                          }`}>
                            {providerSettings?.stripe?.mode === 'live' ? 'Live' : 'Test'} Mode
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Revolut Card */}
                    <div 
                      className={`p-6 rounded-lg border-2 cursor-pointer transition-all ${
                        providerSettings?.active_provider === 'revolut'
                          ? 'border-[#A2FF00] bg-[#A2FF00]/10'
                          : `${isDark ? 'border-white/20 hover:border-white/40' : 'border-gray-200 hover:border-gray-400'}`
                      }`}
                      onClick={() => switchActiveProvider('revolut')}
                    >
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 bg-[#0075EB] rounded-lg flex items-center justify-center">
                            <span className="text-white font-bold text-lg">R</span>
                          </div>
                          <div>
                            <h4 className={`font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Revolut</h4>
                            <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Merchant & Business API</p>
                          </div>
                        </div>
                        {providerSettings?.active_provider === 'revolut' && (
                          <span className="px-2 py-1 rounded text-xs bg-[#A2FF00] text-black font-medium">ACTIVE</span>
                        )}
                      </div>
                      <div className="space-y-2 text-sm">
                        <div className="flex items-center gap-2">
                          {providerSettings?.revolut?.configured ? (
                            <CheckCircle2 className="w-4 h-4 text-green-400" />
                          ) : (
                            <AlertCircle className="w-4 h-4 text-yellow-400" />
                          )}
                          <span className={isDark ? 'text-gray-300' : 'text-gray-600'}>
                            {providerSettings?.revolut?.configured ? 'API Keys Configured' : 'API Keys Not Configured'}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`px-2 py-0.5 rounded text-xs ${
                            providerSettings?.revolut?.mode === 'production' 
                              ? 'bg-red-500/20 text-red-400' 
                              : 'bg-blue-500/20 text-blue-400'
                          }`}>
                            {providerSettings?.revolut?.mode === 'production' ? 'Production' : 'Sandbox'} Mode
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'transactions' && (
              <div>
                <div className="flex items-center gap-4 mb-4">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      placeholder="Search transactions..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className={`pl-10 ${isDark ? 'bg-[#0A0A0A] border-white/20' : ''}`}
                    />
                  </div>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className={isDark ? 'bg-[#0A0A0A]' : 'bg-gray-50'}>
                        <th className={`text-left text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'} uppercase p-3`}>Date</th>
                        <th className={`text-left text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'} uppercase p-3`}>Company</th>
                        <th className={`text-left text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'} uppercase p-3`}>Amount</th>
                        <th className={`text-left text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'} uppercase p-3`}>Method</th>
                        <th className={`text-left text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'} uppercase p-3`}>Session ID</th>
                        <th className={`text-left text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'} uppercase p-3`}>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredTransactions.length === 0 ? (
                        <tr>
                          <td colSpan={6} className="text-center py-8">
                            <DollarSign className={`w-12 h-12 mx-auto mb-3 ${isDark ? 'text-gray-600' : 'text-gray-300'}`} />
                            <p className={isDark ? 'text-gray-400' : 'text-gray-500'}>No transactions found</p>
                          </td>
                        </tr>
                      ) : (
                        filteredTransactions.map((txn, idx) => (
                          <tr key={idx} className={`border-t ${isDark ? 'border-white/5' : 'border-gray-100'}`}>
                            <td className="p-3">
                              <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                {formatDate(txn.created_at)}
                              </p>
                            </td>
                            <td className="p-3">
                              <p className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                {txn.company_name || txn.metadata?.company_name || '-'}
                              </p>
                            </td>
                            <td className="p-3">
                              <p className={`font-mono font-medium ${isDark ? 'text-[#A2FF00]' : 'text-green-600'}`}>
                                {formatCurrency(txn.amount)}
                              </p>
                            </td>
                            <td className="p-3">
                              <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded text-xs ${isDark ? 'bg-white/10 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>
                                {getMethodIcon(txn.payment_method)}
                                {txn.payment_method?.replace('_', ' ')}
                              </span>
                            </td>
                            <td className="p-3">
                              <p className={`text-xs font-mono ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                {txn.session_id ? `${txn.session_id.substring(0, 20)}...` : '-'}
                              </p>
                            </td>
                            <td className="p-3">
                              <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs ${
                                txn.payment_status === 'paid'
                                  ? 'bg-green-500/20 text-green-400'
                                  : 'bg-yellow-500/20 text-yellow-400'
                              }`}>
                                {txn.payment_status === 'paid' ? <CheckCircle2 className="w-3 h-3" /> : <Clock className="w-3 h-3" />}
                                {txn.payment_status}
                              </span>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {activeTab === 'settings' && (
              <div className="space-y-6">
                <div>
                  <h3 className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'} mb-4`}>Payment Settings</h3>
                  
                  <div className="space-y-4">
                    <div className={`p-4 rounded-lg border ${isDark ? 'border-white/10 bg-[#0A0A0A]' : 'border-gray-200 bg-gray-50'}`}>
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <h4 className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>Default Payment Terms</h4>
                          <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Invoice due date after creation</p>
                        </div>
                        <span className={`px-3 py-1 rounded ${isDark ? 'bg-[#A2FF00]/20 text-[#A2FF00]' : 'bg-green-100 text-green-700'} font-medium`}>
                          14 days
                        </span>
                      </div>
                    </div>

                    <div className={`p-4 rounded-lg border ${isDark ? 'border-white/10 bg-[#0A0A0A]' : 'border-gray-200 bg-gray-50'}`}>
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <h4 className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>Default Currency</h4>
                          <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>All invoices will be in this currency</p>
                        </div>
                        <span className={`px-3 py-1 rounded ${isDark ? 'bg-white/10 text-white' : 'bg-gray-200 text-gray-700'} font-mono font-medium`}>
                          GBP (£)
                        </span>
                      </div>
                    </div>

                    <div className={`p-4 rounded-lg border ${isDark ? 'border-white/10 bg-[#0A0A0A]' : 'border-gray-200 bg-gray-50'}`}>
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <h4 className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>Auto-send Receipts</h4>
                          <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Automatically email receipts after payment</p>
                        </div>
                        <span className={`px-3 py-1 rounded bg-green-500/20 text-green-400 font-medium`}>
                          Enabled
                        </span>
                      </div>
                    </div>

                    <div className={`p-4 rounded-lg border ${isDark ? 'border-white/10 bg-[#0A0A0A]' : 'border-gray-200 bg-gray-50'}`}>
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>Bank Details for BACS Transfers</h4>
                          <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Displayed on bank transfer requests</p>
                        </div>
                      </div>
                      <div className={`mt-3 p-3 rounded ${isDark ? 'bg-[#111]' : 'bg-white'} font-mono text-sm space-y-1`}>
                        <p className={isDark ? 'text-gray-300' : 'text-gray-600'}>Account Name: <span className={isDark ? 'text-white' : 'text-gray-900'}>QSECDEF Ltd</span></p>
                        <p className={isDark ? 'text-gray-300' : 'text-gray-600'}>Sort Code: <span className={isDark ? 'text-white' : 'text-gray-900'}>12-34-56</span></p>
                        <p className={isDark ? 'text-gray-300' : 'text-gray-600'}>Account Number: <span className={isDark ? 'text-white' : 'text-gray-900'}>12345678</span></p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      </AdminPinGate>
    </AppLayout>
  );
};

export default StripeAdmin;
